import { mapGetters } from 'vuex'
import lensTechnologies from '~/constants/lensTechnologies'

const props = {
  design: { type: Object, required: true },
  secondaryDesign: { type: Object, required: true },
  customiserMode: { type: Boolean, required: true },
  lensTechSelected: { type: Boolean, default: false },
  showIrisPreview: { type: Boolean, required: true },
  conversionKitEnabled: { type: Boolean, required: true }
}

export const computed = {
  ...mapGetters({
    getPageConfig: 'product/pageConfig',
    getPageMode: 'product/pageMode',
    getBaseSKU: 'product/baseSKU',
    getBestsellers: 'product/bestsellers',
    getProductName: 'product/productName',
    getProductDetails: 'product/product',
    getNormalisedTrackingSKU: 'product/normaliseSKUForFacebook',
    getExtraSKUs: 'product/extraSKUs',
    getBundlePrice: 'bundle/price',
    getBundlePreorderOption: 'bundle/preorderOption',
    getBundleSelections: 'bundle/selections',
    getMinimumProductPrice: 'product/minimumProductPrice',
    getLensTech: 'product/lensTech',
    getFrameType: 'product/frameType',
    getSkuWithFrameType: 'product/changeFrameType',
    getSwappableParts: 'product/swappableParts',
    getPreorderState: 'product/isOnPreorder',
    getPreorderDate: 'product/preorderDate',
    getStockState: 'product/isInStock',
    getSkuWithStockManagedParts: 'product/skuWithStockManagedParts',
    getEntryChoices: 'product/entryChoices',
    getSecondaryEntryChoices: 'product/secondaryEntryChoices',
    getLimitedEditionProductConfig: 'product/limitedEditionProductConfig',
    getSKUWithLensTech: 'product/changeLensTech',
    getReviewSummary: 'review/productSummary',
    getSwatch: 'product/choiceButtonStyling',
    casePromoActive: 'promo/casePromoActive',
    limitedEditionParts: 'product/limitedEditionParts',
    allChoices: 'product/allChoices',
    getActivePromoForProduct: 'promo/activePromoForProduct'
  }),
  productId () { return this.pageData.product_id },
  leMode () { return (this.pageData.skuPrefix || '').includes('-le_') },
  pageMode () { return this.getPageMode(this.skuPrefix) },
  baseSku () { return this.getBaseSKU(this.skuPrefix) },
  bestsellers () { return this.getBestsellers(this.skuPrefix, true) || [] },
  allowLensTechFiltering () {
    return ['classics3', 'sierras', 'renegades'].includes(this.baseSku) && !this.leMode
  },
  showLandingPageLink () {
    const leProductConfig = this.getLimitedEditionProductConfig(this.ecomProduct.sku)
    const landingPage = leProductConfig.landingPage || {}
    return !!(leProductConfig.showLinkToLandingPage && ['published', 'updated'].includes(landingPage._status))
  },
  preorderDate () {
    return this.getPreorderDate([this.sku, this.secondarySku])
  },
  bestsellerSkuField () {
    if (!['vulcans', 'velans'].includes(this.baseSku)) return 'sku'
    return `sku${this.frameType.toUpperCase()}`
  },
  legacyProduct () {
    return ['pacebreakers', 'revolts'].includes(this.baseSku)
  },
  showLifestyleImages () {
    if (this.leMode || this.lePartMode) return false
    if (['lenses', 'conversionkit', 'parts'].includes(this.pageMode)) return false
    if (!['sierras', 'renegades', 'classics3', 'vulcans', 'velans', 'airas', 'snipers', 'vanguards'].includes(this.baseSku)) return false
    return true
  },
  frameType () { return this.getFrameType(this.sku) },
  lensPart () {
    if (this.leMode) return ''
    return Object.keys(this.design).find(part => part.endsWith('le'))
  },
  lensSku () {
    if (!(this.lensPart in this.design)) return null
    return `${this.lensPart}_${this.design[this.lensPart]}`
  },
  lensTech () {
    return this.getLensTech(this.sku)
  },
  lensOptions () {
    const search = this.sortedProductDetails.find(([part, details]) => part === this.lensPart)
    if (!search) return []
    return search[1].options
  },
  lePartMode () { return this.lePart !== null },
  lePartDetails () {
    if (!this.lePartMode) return {}
    return this.limitedEditionParts[this.lePart] || {}
  },
  lePart () {
    return Object.entries(this.design).map(skuParts => skuParts.join('_')).find(skuPart => skuPart in this.limitedEditionParts) || null
  },
  skuPrefix () {
    let skuPrefix = (this.pageData.skuPrefix || '').split('-')[0]
    if (skuPrefix === 'classics2' || skuPrefix === 'classics') skuPrefix = 'classics3'
    return skuPrefix
  },
  allProductDetails () { return this.getProductDetails(this.productId) || {} },
  ecomProduct () { return this.allProductDetails.product || {} },
  productDetails () { return this.allProductDetails.product_details || {} },
  bundleConfig () { return this.allProductDetails.bundle_config || {} },
  sortedProductDetails () { return this.allProductDetails.product_details_sorted || [] },
  productName () {
    // Shorten LE and SS
    if (this.leMode) return (this.ecomProduct.name || '').replace(/Signature Series/ig, '').replace(/Limited Edition/ig, '').replace('McLaren', '<span class="normal-case">Mc</span>Laren')

    if (this.lePartMode) return this.lePartDetails.title

    // Get product name from SKU
    let productName = this.getProductName(this.sku)

    // Add CK suffix
    if (this.conversionKitEnabled && this.frameType !== null) {
      if (['vulcans', 'velans'].includes(this.baseSku)) {
        productName += ` &amp; ${this.frameType === 'ff' ? 'TF' : 'FF'}`
        productName = productName.replace(/(FF &amp; TF|TF &amp; FF)/g, '<nobr>$1</nobr>')
      } else if (['airas'].includes(this.baseSku)) {
        productName += ` &amp; ${this.frameType === 'bf' ? 'ZF' : 'BF'}`
        productName = productName.replace(/(ZF &amp; BF|BF &amp; ZF)/g, '<nobr>$1</nobr>')
      }
    }

    return productName
  },
  extraSKUs () { return this.getExtraSKUs(this.design, this.skuPrefix) },
  price () {
    if (this.leMode) return parseFloat(this.ecomProduct.price)
    let price = this.getBundlePrice(this.bundleConfig, this.designDetails, this.extraSKUs)
    if (this.secondarySku) price += this.getBundlePrice(this.bundleConfig, this.secondaryDesignDetails)
    return price
  },
  minimumProductPrice () {
    return this.getMinimumProductPrice(this.sku)
  },
  sortedParts () {
    return Object.entries(this.productDetails || {}).filter(
      ([_, details]) => details && !!details.options
    ).sort(
      ([_, details]) => details.option_position
    ).map(
      ([part, _]) => part
    )
  },
  sku () {
    if (this.leMode) return this.pageData.skuPrefix

    let sku = this.skuPrefix
    this.sortedParts.forEach((part) => {
      if (part in this.design) sku += `-${part}_${this.design[part]}`
    })
    return sku
  },
  skuWithEmptyParts () {
    if (this.leMode) return this.pageData.skuPrefix

    let sku = this.skuPrefix
    this.sortedParts.forEach((part) => {
      if (part in this.design) sku += `-${part}_${this.design[part]}`
      else if (this.pageMode === 'parts' && !['vle', 'rle', 'snle'].includes(part)) sku += `-${part}_none`
    })
    return sku
  },
  secondarySkuPersistent () {
    let sku = `${this.skuPrefix}ck`
    this.sortedParts.forEach((part) => {
      if (part in this.secondaryDesign) sku += `-${part}_${this.secondaryDesign[part]}`
    })
    return sku
  },
  secondarySku () {
    if (!this.conversionKitEnabled) return null
    return this.secondarySkuPersistent
  },
  designDetails () {
    return Object.fromEntries(Object.entries(this.design).map(([part, choice]) => {
      if (!(part in this.productDetails)) return false
      return [part, this.allChoices[`${part}_${choice}`]]
    }).filter(entry => entry))
  },
  secondaryDesignDetails () {
    if (!this.secondarySku) return {}
    return Object.fromEntries(Object.entries(this.secondaryDesign).map(([part, choice]) => {
      if (!(part in this.productDetails)) return false
      return [part, this.allChoices[`${part}_${choice}`]]
    }).filter(entry => entry))
  },
  swappableParts () { return this.getSwappableParts(this.baseSku) },
  description () {
    return {
      pacebreakers: 'Engineered for high performance, the latest 4KO®pace lens meets our ultra-lightweight frame design for maximum results, whatever your sporting discipline.',
      renegades: 'Style and performance fuse to create a cutting edge design. Featuring colour-accented icons and our trademark 4K Optics® lenses, SunGod Renegades™ have been engineered for every adventure.',
      sierras: 'A classic style with a contemporary twist. Featuring colour-accented icons, Pop-Lock™ Screwless hinges and our trademark 4K Optics® lenses, life looks better with SunGod Sierras™.',
      classics3: 'A timeless style with unrivalled durability, inspired by an endless quest for the next adventure. Featuring our 4K Optics® lenses and trademark Adventureproof® frames, Classics³ have been engineered for every adventure.',
      revolts: 'SunGod Revolts are the world\'s first interchangeable, fully replaceable goggles, built from the highest quality materials and customised by you. Featuring our innovative 4KO®snow lenses that enhance contrast and optimise clarity in all conditions, Revolts are available with 6 refined lens tints and are guaranteed to enhance the way you see the mountain this winter.',
      snipers: 'Enhanced Vision. Effortless Performance. Snipers™ feature our new lens change system and offer increased peripheral vision for the widest view of the mountain. So you can do what you came to do: enjoy the best powder days of your life.',
      vanguards: '20% more vision. Featuring a dual-layer cylindrical design, we\'ve brought the lens closer to your face for market-leading peripheral vision. Vanguards™ are our best goggle yet.',
      velans: 'Ultimate optical precision with an ultralightweight design.',
      airas: 'ZERO frame, maximum perfomance.',
      vulcans: 'Precisely engineered for maximum peripheral vision.'
    }[this.baseSku] || 'Design your own SunGods with 4K Optics® lenses, Adventureproof® frame material and covered by the SunGod Lifetime Guarantee.'
  },
  plainTextProductName () {
    if (this.leMode) return this.ecomProduct.name
    return this.getProductName(this.sku, false)
  },
  preorderPartSelected () {
    const preorderOption = this.getBundlePreorderOption(this.bundleConfig)
    const selections = Object.values(this.getBundleSelections(this.bundleConfig, this.designDetails)).flat()
    const preorderByDefault = preorderOption && selections.includes(`${preorderOption.parts.po_preorder}`)

    const preorderPart = [
      ...Object.values(this.designDetails),
      ...Object.values(this.secondaryDesignDetails)
    ].map((part) => {
      if (!part) return false
      return this.lensTechSelectedOrStatic || !this.getIsLensPart(part.sku) ? this.getPreorderState(part.sku) : this.getAllLensesOnPreorder(part.sku)
    }).filter(preorder => preorder).length > 0

    return preorderByDefault || preorderPart || this.getPreorderState(this.sku)
  },
  reviewSummary () {
    return this.getReviewSummary(this.baseSku)
  },
  isInStock () {
    const prod = this.ecomProduct
    if (prod.type_id === 'simple') return prod.is_in_stock && prod.is_salable
    return this.getStockState(this.sku)
  },
  commonProps () {
    return {
      design: this.design,
      secondaryDesign: this.secondaryDesign,
      customiserMode: this.customiserMode,
      lensTechSelected: this.lensTechSelected,
      showIrisPreview: this.showIrisPreview,
      conversionKitEnabled: this.conversionKitEnabled
    }
  },
  lensTechSelectedOrStatic () {
    return !this.allowLensTechFiltering || this.lensTechSelected
  },
  pageConfig () {
    return this.getPageConfig(this.skuPrefix).result || {}
  },
  gqlQuery () {
    return this.getPageConfig(this.skuPrefix).query
  },
  url () {
    return this.pageConfig.url
  },
  icon () {
    return this.pageConfig.icon
  },
  designImagesOrder () {
    return this.pageConfig.designImagesOrder || [5, 1, 2]
  },
  relatedProducts () {
    return this.pageConfig.relatedProducts || { products: [] }
  },
  inTheBoxItems () {
    return this.pageConfig.inTheBoxItems || []
  },
  quotes () {
    return this.pageConfig.quotes || []
  },
  heroFeatures () {
    return this.pageConfig.heroFeatures || []
  },
  techSpecs () {
    return this.pageConfig.techSpecs || []
  },
  lensPreviewScenes () {
    return this.pageConfig.lensPreviewScenes || []
  },
  lensPreviewConditions () {
    return this.pageConfig.lensPreviewConditions || []
  },
  useGenericReviews () {
    return this.pageConfig.useGenericReviews || false
  },
  rowsBelow () {
    return this.pageConfig.rowsBelow || []
  },
  rowsAbove () {
    return this.pageConfig.rowsAbove || []
  },
  promo () {
    return this.getActivePromoForProduct(this.sku) || {}
  },
  discount () {
    if (!this.promo.value) return 0
    return this.promo.value
  }
}

export const methods = {
  getAllLensTechVariants (sku) {
    return lensTechnologies.map(techDetails => this.getSKUWithLensTech(sku, techDetails.tech))
  },
  getAnyLensesInStock (sku) {
    return this.getAllLensTechVariants(sku).some(this.getStockState)
  },
  getAllLensesOnPreorder (sku) {
    return this.getAllLensTechVariants(sku).every(this.getPreorderState)
  },
  getIsLensPart (sku) {
    return (sku || '').split('_')[0] === this.lensPart
  }
}

export default {
  props,
  computed,
  methods
}